/* OYL Cart Drawer Extras — v2.1 (April 2026)
 * - Rotating reviews
 * - 10-minute order reservation countdown (sessionStorage-persisted)
 * - Delivery estimate
 * - Upsell carousel arrows + quick-add
 *
 * v2.1 change: timer / reviews / carousel re-query the DOM each tick or use event
 * delegation, and a MutationObserver on <cart-drawer> re-pokes one-shot helpers
 * (delivery estimate, carousel button state, immediate countdown tick) whenever
 * the drawer is swapped — e.g. after Dawn's renderContents, our own quickAdd
 * refresh, or the theme.liquid post-PDP-add helper.
 */

(function() {
  'use strict';

  var TOTAL_SECONDS = 10 * 60;
  var STORAGE_KEY = 'oyl_cart_countdown_deadline';
  var countdownDeadline = null;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    startRotatingReviews();
    startCountdown();
    updateDeliveryEstimate();
    bindUpsellCarousel();
    bindUpsellQuickAdd();
    watchDrawerForChanges();
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Rotating reviews — single setInterval that re-queries slides every tick,
   * so it keeps working after a drawer re-render swaps the slide elements.
   * ───────────────────────────────────────────────────────────────────────── */
  function startRotatingReviews() {
    var currentIndex = 0;
    setInterval(function() {
      var slides = document.querySelectorAll('#oyl-drawer-reviews .oyl-review-slide');
      if (slides.length <= 1) return;
      slides.forEach(function(s, i) {
        s.classList.toggle('active', i === currentIndex);
      });
      currentIndex = (currentIndex + 1) % slides.length;
    }, 5000);
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Countdown timer — 10:00 reservation clock, sessionStorage-persisted.
   * Single setInterval. tickCountdown() re-queries [data-countdown] each tick,
   * so it survives drawer re-renders without needing to be re-started.
   * ───────────────────────────────────────────────────────────────────────── */
  function startCountdown() {
    countdownDeadline = getDeadline();
    tickCountdown();
    setInterval(tickCountdown, 1000);
  }

  function getDeadline() {
    try {
      var saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        var d = parseInt(saved, 10);
        if (!isNaN(d) && d > Date.now()) return d;
      }
    } catch (e) { /* sessionStorage blocked — fall through */ }
    var newDeadline = Date.now() + TOTAL_SECONDS * 1000;
    try { sessionStorage.setItem(STORAGE_KEY, String(newDeadline)); } catch (e) {}
    return newDeadline;
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function tickCountdown() {
    var chips = document.querySelectorAll('[data-countdown]');
    if (chips.length === 0) return;
    var remaining = Math.max(0, Math.round((countdownDeadline - Date.now()) / 1000));
    if (remaining <= 0) {
      countdownDeadline = Date.now() + TOTAL_SECONDS * 1000;
      try { sessionStorage.setItem(STORAGE_KEY, String(countdownDeadline)); } catch (e) {}
      remaining = TOTAL_SECONDS;
    }
    var m = Math.floor(remaining / 60);
    var s = remaining % 60;
    var text = pad(m) + ':' + pad(s);
    chips.forEach(function(c) { c.textContent = text; });
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Delivery estimate — today + 5 weekdays. One-shot. Poked by watchDrawerForChanges
   * so it always fills the (fresh) <strong class="oyl-delivery-date"> after refresh.
   * ───────────────────────────────────────────────────────────────────────── */
  function updateDeliveryEstimate() {
    var dateElements = document.querySelectorAll('.oyl-delivery-date');
    if (dateElements.length === 0) return;
    var estimatedDate = calculateDeliveryDate();
    var formattedDate = formatDate(estimatedDate);
    dateElements.forEach(function(el) { el.textContent = formattedDate; });
  }

  function calculateDeliveryDate() {
    // 2 weekdays out (skipping Sat/Sun). Tweak DELIVERY_WEEKDAYS to change.
    var DELIVERY_WEEKDAYS = 2;
    var today = new Date();
    var daysAdded = 0;
    var currentDate = new Date(today);
    while (daysAdded < DELIVERY_WEEKDAYS) {
      currentDate.setDate(currentDate.getDate() + 1);
      var dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) daysAdded++;
    }
    return currentDate;
  }

  function formatDate(date) {
    var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return days[date.getDay()] + ' ' + date.getDate() + ' ' + months[date.getMonth()];
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Upsell carousel arrows — event delegation, so survives drawer re-renders.
   * Disabled state is re-evaluated on scroll (capture, since scroll doesn't
   * bubble), on resize, and on drawer mutations.
   * ───────────────────────────────────────────────────────────────────────── */
  function bindUpsellCarousel() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest && e.target.closest('.oyl-add-to-order-prev, .oyl-add-to-order-next');
      if (!btn) return;
      var scroller = document.getElementById('oyl-upsell-scroller');
      if (!scroller) return;
      var first = scroller.querySelector('.oyl-add-to-order-card');
      var style = getComputedStyle(scroller);
      var gap = parseInt(style.columnGap || style.gap, 10) || 8;
      var step = (first ? first.offsetWidth + gap : 200) * 2;
      var dir = btn.classList.contains('oyl-add-to-order-prev') ? -step : step;
      scroller.scrollBy({ left: dir, behavior: 'smooth' });
    });

    // scroll doesn't bubble, so listen in capture phase from document
    document.addEventListener('scroll', function(e) {
      if (e.target && e.target.id === 'oyl-upsell-scroller') updateCarouselButtons();
    }, true);
    window.addEventListener('resize', updateCarouselButtons);
    updateCarouselButtons();
  }

  function updateCarouselButtons() {
    var scroller = document.getElementById('oyl-upsell-scroller');
    var prev = document.querySelector('.oyl-add-to-order-prev');
    var next = document.querySelector('.oyl-add-to-order-next');
    if (!scroller || !prev || !next) return;
    var max = scroller.scrollWidth - scroller.clientWidth;
    prev.disabled = scroller.scrollLeft <= 1;
    next.disabled = scroller.scrollLeft >= max - 1;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Quick-add "+" buttons on the upsell tiles.
   * Delegated click handler — no rebind needed on refresh.
   * ───────────────────────────────────────────────────────────────────────── */
  function bindUpsellQuickAdd() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest && e.target.closest('.oyl-add-to-order-add');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      quickAdd(variantId, btn);
    });
  }

  function quickAdd(variantId, btn) {
    if (btn.disabled) return;
    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = '…';

    var addUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/') + 'cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript'
      },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    })
      .then(function(r) {
        if (!r.ok) return r.json().then(function(err) { throw err; });
        return r.json();
      })
      .then(function(data) {
        btn.classList.add('is-success');
        btn.textContent = '\u2713'; // ✓
        refreshCartDrawer(data);
        setTimeout(function() {
          btn.textContent = originalText;
          btn.classList.remove('is-success');
          btn.disabled = false;
        }, 1600);
      })
      .catch(function(err) {
        console.error('[OYL cart] quick add failed:', err);
        btn.textContent = '!';
        setTimeout(function() {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1800);
      });
  }

  function refreshCartDrawer(data) {
    var drawer = document.querySelector('cart-drawer');
    var swapped = false;

    if (drawer && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(data);
        swapped = true;
      } catch (e) {
        console.error('[OYL cart] renderContents failed, falling back:', e);
      }
    }

    if (!swapped && data && data.sections && data.sections['cart-drawer']) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(data.sections['cart-drawer'], 'text/html');
      var newInner = doc.querySelector('.drawer__inner');
      var currentInner = document.querySelector('cart-drawer .drawer__inner');
      if (newInner && currentInner) {
        currentInner.innerHTML = newInner.innerHTML;
        swapped = true;
      }
    }

    // The MutationObserver (watchDrawerForChanges) will fire the per-refresh pokes,
    // so we don't duplicate them here.

    // Safety net: after a renderContents swap, sometimes a CSS transition on
    // .oyl-cart registers as a Web Animation that gets stuck at currentTime=0
    // (drawer off-screen). Cancel any running animations so the drawer snaps
    // cleanly to its final transform.
    if (swapped) {
      var panel = document.querySelector('cart-drawer .oyl-cart');
      if (panel && typeof panel.getAnimations === 'function') {
        panel.getAnimations().forEach(function(a){ try { a.cancel(); } catch (e) {} });
      }
    }

    // Update the header cart bubble
    if (data && data.sections && data.sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var pd = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        var first = pd.body.firstElementChild;
        if (first) bubble.innerHTML = first.innerHTML;
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Drawer mutation watcher — whenever the cart-drawer's inner DOM changes
   * (renderContents, manual swap, theme.liquid helper, app-triggered swap),
   * re-poke the one-shot helpers so the new DOM picks up correct state.
   * ───────────────────────────────────────────────────────────────────────── */
  function watchDrawerForChanges() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      // drawer may not be in DOM yet if this runs early — retry shortly
      setTimeout(watchDrawerForChanges, 500);
      return;
    }
    var debounce = null;
    var observer = new MutationObserver(function() {
      clearTimeout(debounce);
      debounce = setTimeout(function() {
        tickCountdown();           // update fresh countdown chip immediately
        updateDeliveryEstimate();  // fill fresh delivery-date element
        updateCarouselButtons();   // recompute prev/next disabled state
      }, 50);
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

})();